import * as React from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Check, ChevronsUpDown, Loader2, Sparkles } from 'lucide-react'
import {
  getApiErrorReason,
  isMainBranch,
  isMergeWorkActive,
  runMergeConflictAgent,
  useGetBranchQuery,
  useGetMainBranchQuery,
  useGetMergeRequestQuery,
  useListBranchesQuery,
  useListMergeRequestsQuery,
  useMergeMergeRequestMutation,
  useOpenMergeRequestMutation,
  usePatchMergeRequestMutation,
  type Branch,
  type CompareResult,
  type EntityResolution,
  type MergeConflictAgentAnswer,
  type MergeConflictAgentQuestion,
  type MergeConflicts,
  type MergeResolutions,
  type StoredResolutions
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Card, CardContent } from '../../components/ui/card'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '../../components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover'
import { ComparePreview } from '../../components/Branches/ComparePreview'
import { MergeConflictResolver } from '../../components/Branches/MergeConflictResolver'
import { buildAgentConflicts } from '../../components/Branches/merge-agent-digest'
import { LazyChatMarkdown } from '../../components/ChatMarkdown/LazyChatMarkdown'
import { useDebouncedValue } from '../../lib/use-debounced-value'
import { cn } from '../../lib/utils'

type MergeNotice = { kind: 'info' | 'error'; text: string }

const TARGET_PAGE_SIZE = 20
const TARGET_SEARCH_DEBOUNCE_MS = 250

const EMPTY_COMPARE: CompareResult = {
  counts: {
    nodesAdded: 0,
    nodesChanged: 0,
    nodesRemoved: 0,
    relsAdded: 0,
    relsChanged: 0,
    relsRemoved: 0
  }
}

function isEligibleTarget(branch: Branch | undefined, sourceId: string): branch is Branch {
  return !!branch && branch.status === 'active' && branch.id !== sourceId
}

function sortTargets(a: Branch, b: Branch): number {
  return Number(b.protected) - Number(a.protected) || a.name.localeCompare(b.name)
}

/**
 * Merge review page (`/branches/merge?source=&target=`). Preview and apply both
 * enqueue durable background work on the merge request (202 Accepted). The page
 * stays put and refetches on merge wake-ups until a terminal result.
 */
export default function MergeReviewPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const sourceId = searchParams.get('source') ?? ''
  const targetParam = searchParams.get('target')
  // Optional caller backlink (e.g. the agent-ingest page). Only honor internal
  // app paths so the param can't be used as an open redirect.
  const returnToParam = searchParams.get('returnTo')
  const returnTo =
    returnToParam && returnToParam.startsWith('/') && !returnToParam.startsWith('//')
      ? returnToParam
      : null
  const returnLabel = searchParams.get('returnLabel') || 'Back'

  const { data: source, isLoading: sourceLoading } = useGetBranchQuery(sourceId, {
    skip: !sourceId
  })
  const { data: mainBranch } = useGetMainBranchQuery()
  const { data: parentBranch } = useGetBranchQuery(source?.parentId ?? '', {
    skip: !source?.parentId
  })
  // Explicit ?target= must resolve by id — never by paging position — so a
  // resumed merge never silently switches to parent/main/first-page.
  const {
    data: explicitTarget,
    isLoading: explicitTargetLoading,
    isError: explicitTargetError
  } = useGetBranchQuery(targetParam ?? '', { skip: !targetParam })
  const [openMergeRequest, openState] = useOpenMergeRequestMutation()
  const [patchMergeRequest] = usePatchMergeRequestMutation()
  const [mergeMergeRequest, mergeState] = useMergeMergeRequestMutation()

  const [message, setMessage] = React.useState('')
  const [notice, setNotice] = React.useState<MergeNotice | null>(null)
  const [requestId, setRequestId] = React.useState<string | null>(null)
  // True from Preview/Merge click until the detail reflects the new run (active
  // or a fresh terminal). Bridges the gap where cache still holds the prior
  // work fields.
  const [pendingPreview, setPendingPreview] = React.useState(false)
  const [pendingApply, setPendingApply] = React.useState(false)
  const previewSnapRef = React.useRef<{
    workStatus?: string
    workFinishedAt?: string
    updatedAt?: string
  }>({})

  // Target picker: server-backed search + offset pages over the user population.
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [targetSearch, setTargetSearch] = React.useState('')
  const [targetPage, setTargetPage] = React.useState(0)
  const debouncedTargetSearch = useDebouncedValue(targetSearch, TARGET_SEARCH_DEBOUNCE_MS)
  const targetQuery = debouncedTargetSearch.trim()
  React.useEffect(() => {
    setTargetPage(0)
  }, [targetQuery])

  const { data: userData, isFetching: targetListFetching } = useListBranchesQuery({
    origin: 'user',
    pageSize: TARGET_PAGE_SIZE,
    offset: targetPage * TARGET_PAGE_SIZE,
    ...(targetQuery ? { q: targetQuery } : {})
  })

  // The resolution map is lifted here so the agent's streamed picks and the
  // user's manual clicks write the same state.
  const [choices, setChoices] = React.useState<Record<string, EntityResolution>>({})
  const [rationales, setRationales] = React.useState<Record<string, string>>({})
  const [agentRunning, setAgentRunning] = React.useState(false)
  const [agentQuestion, setAgentQuestion] = React.useState<MergeConflictAgentQuestion | null>(null)
  const [agentAnswers, setAgentAnswers] = React.useState<MergeConflictAgentAnswer[]>([])
  const [agentSummary, setAgentSummary] = React.useState<string | null>(null)
  const [agentError, setAgentError] = React.useState<string | null>(null)
  const [questionDraft, setQuestionDraft] = React.useState('')
  const agentAbortRef = React.useRef<AbortController | null>(null)

  // Main + fork parent stay available regardless of the current search page.
  const pinnedTargets = React.useMemo(() => {
    if (!source) return []
    const byId = new Map<string, Branch>()
    for (const b of [mainBranch, parentBranch]) {
      if (isEligibleTarget(b, source.id)) byId.set(b.id, b)
    }
    return [...byId.values()].sort(sortTargets)
  }, [mainBranch, parentBranch, source])

  // Current server page only — do not accumulate pages client-side.
  const pageTargets = React.useMemo(() => {
    if (!source) return []
    const pinnedIds = new Set(pinnedTargets.map((b) => b.id))
    return (userData?.branches ?? [])
      .filter((b) => isEligibleTarget(b, source.id) && !pinnedIds.has(b.id))
      .sort(sortTargets)
  }, [userData, pinnedTargets, source])

  const targetTotalSize = userData?.totalSize ?? 0
  const targetTotalPages = Math.max(1, Math.ceil(targetTotalSize / TARGET_PAGE_SIZE))

  const target = React.useMemo(() => {
    if (!source) return undefined
    if (targetParam) {
      // Hold selection until the id lookup settles — never fall back to parent/main.
      if (explicitTargetLoading) return undefined
      if (!isEligibleTarget(explicitTarget, source.id) || explicitTargetError) return undefined
      return explicitTarget
    }
    return (
      pinnedTargets.find((b) => b.id === source.parentId) ??
      pinnedTargets.find(isMainBranch) ??
      pinnedTargets[0] ??
      pageTargets[0]
    )
  }, [
    source,
    targetParam,
    explicitTarget,
    explicitTargetLoading,
    explicitTargetError,
    pinnedTargets,
    pageTargets
  ])

  const targetUnavailable =
    !!source &&
    !!targetParam &&
    !explicitTargetLoading &&
    (!isEligibleTarget(explicitTarget, source.id) || explicitTargetError)

  // The open PR for this pair, if one already exists — so opening the page
  // preloads its stored conflicts/diff/resolutions (a read) before any preview.
  const { data: openRequests } = useListMergeRequestsQuery({ status: 'open' })
  const pairRequest = React.useMemo(
    () =>
      source && target
        ? openRequests?.find((r) => r.sourceId === source.id && r.targetId === target.id)
        : undefined,
    [openRequests, source, target]
  )

  React.useEffect(() => {
    if (pairRequest?.id) setRequestId(pairRequest.id)
  }, [pairRequest?.id])

  const { data: detail } = useGetMergeRequestQuery(requestId ?? '', { skip: !requestId })

  const workActive = isMergeWorkActive(detail?.workStatus)
  React.useEffect(() => {
    if (!pendingPreview || !detail) return
    if (workActive) {
      setPendingPreview(false)
      return
    }
    const snap = previewSnapRef.current
    const terminal = detail.workStatus === 'succeeded' || detail.workStatus === 'failed'
    const fresh =
      detail.workFinishedAt !== snap.workFinishedAt || detail.updatedAt !== snap.updatedAt
    if (terminal && fresh) setPendingPreview(false)
  }, [pendingPreview, workActive, detail])
  React.useEffect(() => {
    if (!pendingApply || !detail) return
    if (workActive && detail.workKind === 'apply') {
      setPendingApply(false)
      return
    }
    if (
      detail.status === 'merged' ||
      (detail.workKind === 'apply' && detail.workStatus === 'failed')
    ) {
      setPendingApply(false)
    }
  }, [pendingApply, workActive, detail])
  const enqueueingPreview = openState.isLoading
  const submitting = mergeState.isLoading
  const isMerged = detail?.status === 'merged'
  const applyWorkActive = pendingApply || submitting || (workActive && detail?.workKind === 'apply')
  const previewWorkActive =
    pendingPreview || enqueueingPreview || (workActive && detail?.workKind !== 'apply')
  const freezeInputs =
    workActive || enqueueingPreview || submitting || pendingPreview || pendingApply || isMerged

  // Review UI only for open requests after preview (or apply that bounced back
  // to review). A succeeded apply leaves status=merged — that must not look
  // like a ready-to-merge preview again.
  const previewReady =
    !!detail &&
    detail.status === 'open' &&
    !workActive &&
    !pendingPreview &&
    !pendingApply &&
    !enqueueingPreview &&
    !submitting &&
    ((detail.workKind === 'preview' &&
      (detail.workStatus === 'succeeded' || detail.workStatus === 'failed')) ||
      (detail.workKind === 'apply' && detail.workStatus === 'failed') ||
      // Resumed request with materialized data before any work fields existed.
      (!detail.workKind && !!(detail.conflicts || detail.incoming || detail.noChanges)))

  const conflictTotal =
    (detail?.conflicts?.nodes.length ?? 0) +
    (detail?.conflicts?.rels.length ?? 0) +
    (detail?.conflicts?.postgres?.length ?? 0)
  // Hard failure with nothing to review — conflict/no-change bounce-backs still
  // carry review data and render those branches instead.
  const previewFailed =
    previewReady && detail.workStatus === 'failed' && !detail.noChanges && conflictTotal === 0
  const noChanges = previewReady && !!detail.noChanges && !previewFailed
  const conflicts: MergeConflicts | null =
    previewReady && !previewFailed && !noChanges
      ? conflictTotal > 0
        ? detail.conflicts
        : null
      : null
  const incoming = detail?.incoming ?? EMPTY_COMPARE
  const hasPreviewed =
    !!detail || pendingPreview || enqueueingPreview || workActive || !!openState.error

  // The request whose saved resolutions we've already loaded — so an autosave's
  // refetch never re-seeds (and clobbers) the in-progress picks.
  const seededRequestRef = React.useRef<string | null>(null)

  const setTarget = (id: string) => {
    if (freezeInputs) return
    openState.reset()
    setRequestId(null)
    setPendingPreview(false)
    setPendingApply(false)
    setNotice(null)
    seededRequestRef.current = null
    const next = new URLSearchParams(searchParams)
    next.set('target', id)
    setSearchParams(next, { replace: true })
  }

  const runPreview = () => {
    if (!target || !sourceId || freezeInputs) return
    setNotice(null)
    previewSnapRef.current = {
      workStatus: detail?.workStatus,
      workFinishedAt: detail?.workFinishedAt,
      updatedAt: detail?.updatedAt
    }
    setPendingPreview(true)
    void openMergeRequest({ targetId: target.id, sourceId })
      .unwrap()
      .then((accepted) => {
        setRequestId(accepted.id)
      })
      .catch((err) => {
        setPendingPreview(false)
        setNotice({ kind: 'error', text: extractMergeError(err) })
      })
  }

  const conflictIds = React.useMemo(
    () =>
      new Set<string>([
        ...(conflicts?.nodes ?? []).map((c) => c.id),
        ...(conflicts?.rels ?? []).map((c) => c.id),
        ...(conflicts?.postgres ?? []).map((c) => c.id)
      ]),
    [conflicts]
  )
  const conflictCount =
    (conflicts?.nodes.length ?? 0) +
    (conflicts?.rels.length ?? 0) +
    (conflicts?.postgres?.length ?? 0)

  // A new conflict set invalidates any agent session and the picks made against
  // the previous set.
  React.useEffect(() => {
    agentAbortRef.current?.abort()
    agentAbortRef.current = null
    setChoices({})
    setRationales({})
    setAgentAnswers([])
    setAgentQuestion(null)
    setAgentSummary(null)
    setAgentError(null)
    setAgentRunning(false)
    setQuestionDraft('')
    seededRequestRef.current = null
  }, [conflicts])

  // Abort an in-flight agent session if the page unmounts.
  React.useEffect(() => () => agentAbortRef.current?.abort(), [])

  // Seed the resolver from the request's saved resolutions, once per request —
  // a resumed PR (this user's or a teammate's) picks up where it was left off.
  React.useEffect(() => {
    if (!detail || !conflicts) return
    if (seededRequestRef.current === detail.id) return
    seededRequestRef.current = detail.id
    const seeded = storedToChoices(detail.resolutions)
    if (Object.keys(seeded.choices).length === 0) return
    setChoices(seeded.choices)
    setRationales(seeded.rationales)
  }, [detail, conflicts])

  // Autosave the in-progress picks (debounced). Held until the request's saved
  // resolutions have loaded (seeded), so a fresh mount never overwrites them.
  // The latest payload is parked in a ref so an unmount before the debounce
  // elapses (e.g. navigating back to Branches right after picking) still flushes.
  const pendingSaveRef = React.useRef<{ id: string; resolutions: StoredResolutions } | null>(null)
  React.useEffect(() => {
    if (!requestId || !conflicts || workActive || seededRequestRef.current !== requestId) return
    const payload = {
      id: requestId,
      resolutions: choicesToStored(choices, rationales, conflicts)
    }
    pendingSaveRef.current = payload
    const handle = setTimeout(() => {
      pendingSaveRef.current = null
      void patchMergeRequest(payload)
    }, 800)
    return () => clearTimeout(handle)
  }, [choices, rationales, requestId, conflicts, workActive, patchMergeRequest])

  // Flush any debounced-but-unsaved picks when the page unmounts.
  const patchRef = React.useRef(patchMergeRequest)
  patchRef.current = patchMergeRequest
  React.useEffect(
    () => () => {
      if (pendingSaveRef.current) {
        void patchRef.current(pendingSaveRef.current)
        pendingSaveRef.current = null
      }
    },
    []
  )

  // Run one turn-batch of the agent: stream picks into the lifted resolver and
  // surface a question if it pauses. Advisory only — nothing merges here.
  const runAgent = async (answers: MergeConflictAgentAnswer[]) => {
    if (!conflicts || !target || !source || agentRunning || freezeInputs) return
    agentAbortRef.current?.abort()
    const controller = new AbortController()
    agentAbortRef.current = controller
    setAgentRunning(true)
    setAgentQuestion(null)
    setAgentError(null)
    try {
      await runMergeConflictAgent(
        {
          targetName: target.name,
          sourceName: source.name,
          conflicts: buildAgentConflicts(conflicts),
          resolvedIds: Object.keys(choices),
          answers
        },
        {
          onPick: ({ id, resolution, rationale }) => {
            setChoices((prev) => ({ ...prev, [id]: resolution }))
            if (rationale) setRationales((prev) => ({ ...prev, [id]: rationale }))
          },
          onQuestion: (question) => setAgentQuestion(question),
          onFinalized: (summary) => setAgentSummary(summary),
          onError: (msg) => setAgentError(msg)
        },
        controller.signal
      )
    } catch (err) {
      if (!controller.signal.aborted) {
        setAgentError(err instanceof Error ? err.message : 'The agent could not run.')
      }
    } finally {
      if (agentAbortRef.current === controller) {
        agentAbortRef.current = null
        setAgentRunning(false)
      }
    }
  }

  const answerAgentQuestion = (answer: string) => {
    if (!agentQuestion || !answer.trim()) return
    const entry: MergeConflictAgentAnswer = {
      question: agentQuestion.question,
      answer: answer.trim(),
      ...(agentQuestion.id ? { id: agentQuestion.id } : {})
    }
    const next = [...agentAnswers, entry]
    setAgentAnswers(next)
    setAgentQuestion(null)
    setQuestionDraft('')
    void runAgent(next)
  }

  const runMerge = async (resolutions?: MergeResolutions) => {
    if (!target || !source || freezeInputs || !requestId) return
    setNotice(null)
    setPendingApply(true)
    try {
      // Flush the latest picks before enqueueing apply so the worker reads them.
      if (resolutions || Object.keys(choices).length > 0) {
        pendingSaveRef.current = null
        await patchMergeRequest({
          id: requestId,
          resolutions: choicesToStored(choices, rationales, conflicts)
        }).unwrap()
      }
      await mergeMergeRequest({
        id: requestId,
        ...(message.trim() ? { message: message.trim() } : {})
      }).unwrap()
      // Branch switch happens in the cache-coherence bridge when apply
      // actually lands — not on enqueue — so it works even if the user leaves.
    } catch (err) {
      setPendingApply(false)
      switch (getApiErrorReason(err)) {
        case 'NOTHING_TO_MERGE':
          setNotice({ kind: 'info', text: `${target.name} already contains ${source.name}.` })
          break
        case 'BRANCH_NOT_ACTIVE':
          setNotice({ kind: 'error', text: 'One of the branches is no longer active.' })
          break
        default:
          setNotice({ kind: 'error', text: extractMergeError(err) })
      }
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-card">
      <header>
        <Link
          to={returnTo ?? '/branches/merges'}
          className="mb-2 inline-flex items-center gap-1 text-xs text-ink-3 hover:text-ink"
        >
          <ArrowLeft className="size-3.5" />
          {returnTo ? returnLabel : 'Merges'}
        </Link>
        <h1 className="m-0 text-2xl font-semibold text-ink">
          {conflicts ? 'Resolve merge conflicts' : 'Merge branch'}
        </h1>
        <p className="mt-1 text-sm text-ink-3">
          {conflicts
            ? 'Both branches changed these entities. Resolve all conflicts in order to merge.'
            : 'Brings everything committed on the source branch into the target; the merged branch is then retired.'}
        </p>
      </header>

      {!sourceId || (!sourceLoading && !source) ? (
        <Card>
          <CardContent className="py-6 text-sm text-ink-3">
            No source branch to merge.{' '}
            <Link to="/settings?tab=branches" className="text-brand underline">
              Pick a branch
            </Link>
            .
          </CardContent>
        </Card>
      ) : !source ? (
        <Card>
          <CardContent className="flex items-center gap-2 py-6 text-sm text-ink-3">
            <Loader2 className="size-4 animate-spin" />
            Loading branch…
          </CardContent>
        </Card>
      ) : (
        <Card className="gap-3 py-4">
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm text-ink">{source.name}</span>
              <Label htmlFor="merge-target" className="text-xs text-ink-3">
                into
              </Label>
              <Popover
                open={pickerOpen}
                onOpenChange={(next) => {
                  if (freezeInputs) return
                  setPickerOpen(next)
                  if (!next) setTargetSearch('')
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    id="merge-target"
                    type="button"
                    variant="outline"
                    size="sm"
                    role="combobox"
                    aria-expanded={pickerOpen}
                    aria-label="Target branch"
                    disabled={freezeInputs}
                    className="min-w-40 justify-between font-mono text-xs"
                  >
                    <span className="truncate">
                      {target?.name ??
                        (explicitTargetLoading
                          ? 'Loading…'
                          : targetUnavailable
                            ? 'Unavailable target'
                            : 'Select target')}
                    </span>
                    <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-72 p-0">
                  {/* Server-side name filter — disable cmdk's client-side filter. */}
                  <Command shouldFilter={false}>
                    <CommandInput
                      value={targetSearch}
                      onValueChange={(value) => {
                        setTargetSearch(value)
                        setTargetPage(0)
                      }}
                      placeholder="Search branches…"
                      aria-label="Search target branches"
                    />
                    <CommandList>
                      {targetListFetching ? (
                        <div className="flex items-center gap-2 px-3 py-2.5 text-xs text-ink-3">
                          <Loader2 className="size-3.5 animate-spin" aria-hidden />
                          Loading branches…
                        </div>
                      ) : null}
                      {!targetListFetching &&
                      pinnedTargets.length === 0 &&
                      pageTargets.length === 0 ? (
                        <CommandEmpty>
                          {targetQuery
                            ? `No branches match “${targetQuery}”.`
                            : 'No target branches available.'}
                        </CommandEmpty>
                      ) : null}
                      {pinnedTargets.length > 0 ? (
                        <CommandGroup heading="Pinned">
                          {pinnedTargets.map((b) => (
                            <CommandItem
                              key={b.id}
                              value={b.id}
                              onSelect={() => {
                                setTarget(b.id)
                                setPickerOpen(false)
                                setTargetSearch('')
                              }}
                            >
                              <Check
                                className={cn(
                                  'size-3.5',
                                  target?.id === b.id ? 'opacity-100' : 'opacity-0'
                                )}
                                aria-hidden
                              />
                              <span className="truncate font-mono text-xs">{b.name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ) : null}
                      {pageTargets.length > 0 ? (
                        <CommandGroup heading="Branches">
                          {pageTargets.map((b) => (
                            <CommandItem
                              key={b.id}
                              value={b.id}
                              onSelect={() => {
                                setTarget(b.id)
                                setPickerOpen(false)
                                setTargetSearch('')
                              }}
                            >
                              <Check
                                className={cn(
                                  'size-3.5',
                                  target?.id === b.id ? 'opacity-100' : 'opacity-0'
                                )}
                                aria-hidden
                              />
                              <span className="truncate font-mono text-xs">{b.name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ) : null}
                    </CommandList>
                    {targetTotalSize > TARGET_PAGE_SIZE ? (
                      <div className="flex items-center justify-between gap-2 border-t border-line px-2 py-1.5">
                        <span className="text-11 text-ink-3">
                          {targetPage * TARGET_PAGE_SIZE + 1}–
                          {Math.min((targetPage + 1) * TARGET_PAGE_SIZE, targetTotalSize)} of{' '}
                          {targetTotalSize}
                        </span>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            disabled={targetPage <= 0 || targetListFetching}
                            onClick={() => setTargetPage((p) => Math.max(0, p - 1))}
                          >
                            Previous
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            disabled={targetPage + 1 >= targetTotalPages || targetListFetching}
                            onClick={() =>
                              setTargetPage((p) => Math.min(targetTotalPages - 1, p + 1))
                            }
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </Command>
                </PopoverContent>
              </Popover>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={runPreview}
                disabled={freezeInputs || !target || targetUnavailable || explicitTargetLoading}
              >
                {enqueueingPreview || (workActive && detail?.workKind === 'preview') ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : null}
                {hasPreviewed || openState.error ? 'Refresh preview' : 'Preview'}
              </Button>
            </div>

            {targetUnavailable ? (
              <p role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
                That target branch is unavailable. Choose another target.
              </p>
            ) : null}

            <Input
              value={message}
              onChange={(event) => {
                setMessage(event.target.value)
                setNotice(null)
              }}
              placeholder="Merge message (optional)"
              aria-label="Merge message"
              autoComplete="off"
              disabled={freezeInputs}
            />

            {notice ? (
              <div
                role={notice.kind === 'info' ? 'status' : 'alert'}
                className={
                  notice.kind === 'info'
                    ? 'rounded-md bg-st-idle-bg/40 px-3 py-2 text-xs text-ink-3'
                    : 'rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down'
                }
              >
                {notice.text}
              </div>
            ) : null}

            {previewWorkActive ? (
              <p
                role="status"
                className="flex items-center gap-2 rounded-md border border-line px-3 py-3 text-xs text-ink-3"
              >
                <Loader2 className="size-3 animate-spin" />
                Preview running in background.
              </p>
            ) : applyWorkActive ? (
              <p
                role="status"
                className="flex items-center gap-2 rounded-md border border-line px-3 py-3 text-xs text-ink-3"
              >
                <Loader2 className="size-3 animate-spin" />
                Merge running in background.
              </p>
            ) : isMerged ? (
              <div className="flex flex-col gap-3">
                <p role="status" className="rounded-md bg-st-ok-bg/40 px-3 py-3 text-xs text-st-ok">
                  Merged
                  {detail.resultCommitHash ? ` · ${detail.resultCommitHash.slice(0, 8)}` : ''}.
                </p>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/branches/merges')}
                  >
                    Back to merges
                  </Button>
                </div>
              </div>
            ) : openState.error && !detail ? (
              <p role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
                Failed to start the preview. Retry, or pick a different target.
              </p>
            ) : !detail ? (
              <p className="rounded-md border border-line px-3 py-3 text-xs text-ink-3">
                Preview the merge to check for conflicts and see what it brings in.
              </p>
            ) : previewFailed ? (
              <div className="flex flex-col gap-3">
                <p role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
                  {detail.workErrorMessage ||
                    detail.workErrorReason ||
                    'Preview failed. Refresh preview to retry.'}
                </p>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/branches/merges')}
                  >
                    Back to merges
                  </Button>
                </div>
              </div>
            ) : noChanges ? (
              <div className="flex flex-col gap-3">
                <p
                  role="status"
                  className="rounded-md bg-st-idle-bg/40 px-3 py-3 text-xs text-ink-3"
                >
                  Nothing to merge — {target?.name ?? 'the target'} already has everything on{' '}
                  {source.name}.
                </p>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/branches/merges')}
                  >
                    Back to merges
                  </Button>
                </div>
              </div>
            ) : conflicts ? (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2 rounded-md border border-line p-3">
                  <span className="text-xs font-semibold text-ink">Changes</span>
                  <ComparePreview
                    result={incoming}
                    excludeIds={conflictIds}
                    emptyMessage="No non-conflicting changes."
                  />
                </div>

                <div className="flex min-w-0 flex-col gap-2 rounded-md border border-st-down/40 bg-st-down-bg/15 p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-st-down">Conflicts</span>
                    <span className="rounded-full bg-st-down-bg px-2 py-0.5 text-11 font-medium text-st-down">
                      {conflictCount} {conflictCount === 1 ? 'conflict' : 'conflicts'}
                    </span>
                    <span className="text-11 text-ink-3">
                      both branches changed these — pick a side or ask the agent
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      className="ml-auto"
                      disabled={agentRunning || freezeInputs}
                      onClick={() => void runAgent(agentAnswers)}
                    >
                      {agentRunning ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="size-3.5" />
                      )}
                      {agentRunning ? 'Agent resolving…' : 'Resolve with agent'}
                    </Button>
                  </div>

                  {agentError ? (
                    <p
                      role="alert"
                      className="rounded-md bg-st-down-bg px-3 py-2 text-11 text-st-down"
                    >
                      {agentError}
                    </p>
                  ) : null}

                  {agentQuestion ? (
                    <div className="flex min-w-0 flex-col gap-2 rounded-md border border-brand/40 bg-brand-soft/30 p-3">
                      <div className="text-xs text-ink">
                        <span className="font-medium text-brand">Agent asks</span>
                        {agentQuestion.id ? (
                          <span className="ml-1.5 font-mono text-10 text-ink-3">
                            re: {agentQuestion.id}
                          </span>
                        ) : null}
                        <div className="chat-markdown mt-1 min-w-0 break-words [&>*:first-of-type]:mt-0 [&>*:last-child]:mb-0">
                          <LazyChatMarkdown>{agentQuestion.question}</LazyChatMarkdown>
                        </div>
                      </div>
                      {agentQuestion.options && agentQuestion.options.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {agentQuestion.options.map((opt) => (
                            <Button
                              key={opt}
                              type="button"
                              variant="outline"
                              size="xs"
                              disabled={agentRunning || freezeInputs}
                              onClick={() => answerAgentQuestion(opt)}
                            >
                              {opt}
                            </Button>
                          ))}
                        </div>
                      ) : null}
                      <form
                        className="flex items-center gap-2"
                        onSubmit={(event) => {
                          event.preventDefault()
                          answerAgentQuestion(questionDraft)
                        }}
                      >
                        <Input
                          value={questionDraft}
                          onChange={(event) => setQuestionDraft(event.target.value)}
                          placeholder="Type an answer…"
                          aria-label="Answer the agent"
                          autoComplete="off"
                          disabled={agentRunning || freezeInputs}
                          className="min-w-0 flex-1"
                        />
                        <Button
                          type="submit"
                          size="sm"
                          disabled={agentRunning || freezeInputs || !questionDraft.trim()}
                        >
                          Send
                        </Button>
                      </form>
                    </div>
                  ) : agentSummary && !agentRunning ? (
                    <div
                      role="status"
                      className="rounded-md bg-brand-soft/30 px-3 py-2 text-11 text-ink-3"
                    >
                      <span className="font-medium text-brand">Agent</span>
                      <div className="chat-markdown mt-0.5 min-w-0 break-words [&>*:first-of-type]:mt-0 [&>*:last-child]:mb-0">
                        <LazyChatMarkdown>{agentSummary}</LazyChatMarkdown>
                      </div>
                      <span>Review the picks below and merge.</span>
                    </div>
                  ) : null}

                  <MergeConflictResolver
                    conflicts={conflicts}
                    targetName={target?.name ?? 'target'}
                    sourceName={source.name}
                    submitting={freezeInputs}
                    choices={choices}
                    onChoicesChange={setChoices}
                    rationales={rationales}
                    onCancel={() => navigate('/branches/merges')}
                    onSubmit={(resolutions) => void runMerge(resolutions)}
                  />
                </div>
              </div>
            ) : previewReady ? (
              <>
                <div className="flex flex-col gap-2 rounded-md border border-line p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-ink">Changes</span>
                    <span className="rounded-full bg-st-ok-bg px-2 py-0.5 text-11 font-medium text-st-ok">
                      No conflicts
                    </span>
                  </div>
                  <ComparePreview result={incoming} />
                </div>

                <div className="flex items-center justify-end gap-2">
                  {submitting ? (
                    <span className="mr-auto flex items-center gap-2 text-xs text-ink-3">
                      <Loader2 className="size-3 animate-spin" />
                      Starting merge…
                    </span>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/branches/merges')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={freezeInputs || !target}
                    onClick={() => void runMerge()}
                  >
                    {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
                    Merge
                  </Button>
                </div>
              </>
            ) : (
              <p className="rounded-md border border-line px-3 py-3 text-xs text-ink-3">
                Preview the merge to check for conflicts and see what it brings in.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

/**
 * Split the flat choices map into the persisted {nodes, rels, provenance} shape:
 * each id keyed by whether it is a node or rel conflict, with a provenance entry
 * marking agent picks (those carrying a rationale) vs human ones.
 */
function choicesToStored(
  choices: Record<string, EntityResolution>,
  rationales: Record<string, string>,
  conflicts: MergeConflicts | null
): StoredResolutions {
  const nodeIds = new Set((conflicts?.nodes ?? []).map((c) => c.id))
  const postgresIds = new Set((conflicts?.postgres ?? []).map((c) => c.id))
  const nodes: Record<string, EntityResolution> = {}
  const rels: Record<string, EntityResolution> = {}
  const postgres: Record<string, 'ours' | 'theirs'> = {}
  for (const [id, res] of Object.entries(choices)) {
    if (nodeIds.has(id)) nodes[id] = res
    else if (postgresIds.has(id)) {
      if (res === 'ours' || res === 'theirs') postgres[id] = res
    } else rels[id] = res
  }
  const provenance: Record<string, { by: 'human' | 'agent'; rationale?: string }> = {}
  for (const id of Object.keys(choices)) {
    provenance[id] = rationales[id] ? { by: 'agent', rationale: rationales[id] } : { by: 'human' }
  }
  return postgresIds.size > 0 ? { nodes, rels, postgres, provenance } : { nodes, rels, provenance }
}

/** Rebuild the flat choices + agent rationales from a persisted resolution set. */
function storedToChoices(stored: StoredResolutions | undefined): {
  choices: Record<string, EntityResolution>
  rationales: Record<string, string>
} {
  const choices: Record<string, EntityResolution> = {
    ...(stored?.nodes ?? {}),
    ...(stored?.rels ?? {}),
    ...(stored?.postgres ?? {})
  }
  const rationales: Record<string, string> = {}
  for (const [id, p] of Object.entries(stored?.provenance ?? {})) {
    if (p.by === 'agent' && p.rationale) rationales[id] = p.rationale
  }
  return { choices, rationales }
}

function extractMergeError(err: unknown): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: unknown }).data
    if (data && typeof data === 'object' && 'message' in data) {
      const message = (data as { message?: unknown }).message
      if (typeof message === 'string' && message.length > 0) return message
    }
  }
  return 'Could not merge.'
}
